import type { AdminPiecesSummary } from '@boardflight/shared';
import { api } from '../api.js';
import { Async, Panel, useAsync } from '../ui.js';

/**
 * What players own (`docs/model-intro-plan.md` P1).
 *
 * The read side of the piece-inventory mirror, and the only one anywhere: the public API has a
 * write route and no read route, because a client that could be told what it owns would be a
 * client that could disagree with the room it is standing in. The device owns the inventory; this
 * is BoardFlight's copy of it.
 *
 * ## The denominator is the screen's most important number
 *
 * Two things make this a sample rather than a census, and both are structural rather than
 * temporary. A Board that has not paired has no `boardInfoHash`, so it has no `devices` row and
 * cannot be counted at all; and a Board with nobody signed in has no principal to hang a report
 * on. So this is what BoardFlight's own client saw on Boards that paired **and** signed in — the
 * same caveat `docs/store-plan.md` §5.1 puts beside the store stats, and it is said here for the
 * same reason: a number that looks like a census and is not is worse than no number.
 *
 * Percentages are taken over **answered**, never over `boards`, and both figures are on screen so
 * nobody has to work out which they are reading.
 *
 * ## Three states, kept apart
 *
 * "Never asked" and "answered: none" are different rows here because they are different rows in
 * the client, which is careful never to say a game is incompatible with a Board that simply never
 * answered. Folding them together would throw away the distinction the whole feature is built on.
 */
export function Pieces() {
  const state = useAsync(() => api<AdminPiecesSummary>('/v1/admin/pieces'), []);

  return (
    <Panel title="Pieces">
      <Async state={state}>
        {(data) => {
          const unanswered = Math.max(0, data.boards - data.answered);
          // Over `answered`, never over `boards` — see the header. Zero answers means no
          // percentage at all rather than a column of NaN or a misleading 0%.
          const share = (n: number): string =>
            data.answered === 0 ? '—' : `${Math.round((n / data.answered) * 100)}%`;

          return (
            <>
              <div className="stats">
                <div className="stat"><span className="stat-label">Boards known</span><span className="stat-value">{data.boards}</span></div>
                <div className="stat"><span className="stat-label">Answered</span><span className="stat-value">{data.answered}</span></div>
                <div className="stat"><span className="stat-label">Never asked</span><span className="stat-value">{unanswered}</span></div>
                <div className="stat"><span className="stat-label">Said none</span><span className="stat-value">{data.empty}</span></div>
                <div className="stat"><span className="stat-label">Library games</span><span className="stat-value">{data.libraryGames}</span></div>
              </div>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Set</th>
                      <th className="num">Boards with it</th>
                      <th className="num" title="Of the Boards that answered">Share</th>
                      <th className="num" title="Library games whose primary model needs this set">Games that need it</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.sets.map((set) => (
                      <tr key={set.key}>
                        <td>
                          {set.display}
                          {/* A box this build's registry has never heard of — a client from a
                              later release reported it. Counted under its own name rather than
                              refused, and flagged so nobody reads it as a typo. */}
                          {set.known ? null : (
                            <span className="pill" style={{ marginLeft: 6 }}>newer client</span>
                          )}
                        </td>
                        <td className="num">{set.boards}</td>
                        <td className="num muted">{share(set.boards)}</td>
                        <td className="num">{set.games || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="note">
                What Boards that paired and signed in say is in the room: a sample, not a census. Shares are of the
                Boards that answered. Nothing here is sent back to a device.
              </p>
            </>
          );
        }}
      </Async>
    </Panel>
  );
}
